import React, { useState, useCallback, useRef, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import { yandexReachGoal, amplitudeSendEvent } from 'cityads-v3/utils/counterUtils';
import {
  useOfferHasRewardCalculator,
  useRewardCalcData,
  useOfferData,
  useLoadReward,
} from 'cityads-v3/hooks/offers'
import { useShortLinks } from 'cityads-v3/hooks/shortLink'

import { IRewardInfo, IOfferRewardState, TRewardItem, TRewardItemError } from 'cityads-v3/types/offerReward'
import { RootState } from 'cityads-v3/types/reducer'

import classes from './Reward.scss'

import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import ClearIcon from '@mui/icons-material/ClearOutlined'
import CopyTextField from 'cityads-v3/components/CopyTextField/CopyTextField'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import LinearProgress from '@mui/material/LinearProgress'
import OfferGetLink from 'cityads-v3/containers/webmaster/offers/OfferGetLink'
import ReportIcon from '@mui/icons-material/ReportOutlined'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

function linkToCode(link: string, code: string): string {
  return `${code}&url=${encodeURIComponent(link)}`
}

export default function Reward({ offerId }: ({ offerId: number })): JSX.Element {
  const { t } = useTranslation(['Offers', 'main']);
  const offer = useOfferData(offerId);
  const offerRewards = useSelector((state: IOfferRewardState) => state.offerReward.offerRewards);
  const hasRewardCalc = useOfferHasRewardCalculator(offerId);
  const rewardInfo: IRewardInfo = useRewardCalcData(offerId);
  const [ code, setCode ] = useState('');
  const [ deeplinksInput, setDeeplinksInput ] = useState<string>('');
  const [ links, setLinks ] = useState<string[]>([]);
  const firstEditRef = useRef(null);
  const copyTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [ isShortLink, setIsShortLink ] = useState(false);
  const shortLinks = useSelector((state: RootState) => state.shortLink.shortLinks)

  const linksWithReward = useMemo(() => links.map(url => {
    const reward = offerRewards[url];
    if (!reward || !reward.commission) return '';
    return linkToCode(reward.url, code);
  }).filter(url => url), [code, links, offerRewards])
  useShortLinks(linksWithReward, isShortLink);

  const copyAllText = useMemo(() => {
    if (isShortLink) {
      return linksWithReward.map(link => shortLinks[link] ?? '').join('\n');
    }
    return linksWithReward.join('\n');
  }, [isShortLink, linksWithReward, shortLinks]);

  const onChangeLinks = useCallback((evt: React.ChangeEvent<HTMLInputElement>) => {
    if (!firstEditRef.current) {
      firstEditRef.current = true;
      const eventData = { offerId };
      yandexReachGoal('REWARD_DEEPLINKS_CHANGE', eventData);
      amplitudeSendEvent('makeActions', 'd-v2-e12', 'offers', 'REWARD_DEEPLINKS_CHANGE', eventData);
    }

    setDeeplinksInput(evt.target.value);
    setLinks(
      evt.target.value?.split('\n')
        .filter(link => link.trim())
        .slice(0, rewardInfo.linksLimit)
    );
  }, [offerId, rewardInfo.linksLimit])

  const handleCopyAll = useCallback(() => {
    copyTextareaRef?.current?.select();
    document.execCommand('copy');
    const eventData = { offerId };
    yandexReachGoal('REWARD_COPY_ALL_LINKS', eventData);
    amplitudeSendEvent('makeActions', 'd-v2-e11', 'offers', 'REWARD_COPY_ALL_LINKS', eventData);
  }, [offerId])

  const handleClearDeeplinks = useCallback(() => {
    setDeeplinksInput('');
    setLinks([]);
  }, [])

  const handleShortAll = useCallback(() => {
    const newValue = !isShortLink;
    setIsShortLink(newValue)
  }, [isShortLink])

  if (!hasRewardCalc) {
    return null;
  }

  return (
    <div className={classes.container}>
      <Typography classes={{root: classes.rewardfor}}>{t('rewardFor', {offerName: offer.translated_name ?? offer.name})}</Typography>
      {rewardInfo.description && (
        <Typography variant="body2">{rewardInfo.description}</Typography>
      )}

      <OfferGetLink
        className={classes.getLink}
        offerId={+offerId}
        isDeeplinkEnabled={false}
        onChangeCode={setCode}
        showCode={false}
        showLinksSelector={false}
      />

      <Box mt={1}>
        <Typography variant="button" color="textSecondary">
          {t('rewardDeeplinksCaption')}
        </Typography>
      </Box>

      <TextField
        fullWidth
        multiline
        label={t('deeplinks')}
        rows={1}
        maxRows={rewardInfo.linksLimit}
        onChange={onChangeLinks}
        value={deeplinksInput}
        InputProps={{
          endAdornment: deeplinksInput.length ? (
            <InputAdornment position="end">
              <IconButton onClick={handleClearDeeplinks} size="large">
                <ClearIcon/>
              </IconButton>
            </InputAdornment>
          ) : null
        }}
      />

      <div className={classes.prelinks}>
        {rewardInfo.warning && (
          <div className={classes.warning}>
            <ReportIcon/>
            {rewardInfo.warning}
          </div>
        )}

        <div>
          <Typography color="textSecondary" variant="body2" className={classes.deeplinkHelperText}>
            {t('rewardDeeplinksCounter', {count: links.length, limit: rewardInfo.linksLimit})}
          </Typography>
        </div>
      </div>

      <div className={classes.rewards}>
        {links.map((link, idx) => (
          <RewardItem
            key={idx}
            link={link}
            code={code}
            rewardInfo={rewardInfo}
            isShortLink={isShortLink}
          />
        ))}
      </div>

      {copyAllText ? (
        <div className={classes.actions}>
          <textarea readOnly className={classes.copyAllTextarea} ref={copyTextareaRef} value={copyAllText}/>
          <Button
            color="primary"
            onClick={handleShortAll}
          >
            {isShortLink ? t('exports.getFullLinks') : t('exports.getShortLinks')}
          </Button>
          <Box ml={1}>
            <Button
              color="primary"
              variant="contained"
              onClick={handleCopyAll}
            >{t('main:buttons.copyAll')}</Button>
          </Box>
        </div>
      ) : null}

    </div>
  )
}

Reward.propTypes = {
  offerId: PropTypes.number
}

function RewardItem({ link, code, rewardInfo, isShortLink }: TRewardItem) {
  const { rewardData, notLoaded, isLoading } = useLoadReward(link);
  const handleCopyText = useCallback(() => {
    const eventData = { offerId: rewardInfo.offerId };
    yandexReachGoal('REWARD_COPY_LINK', eventData);
    amplitudeSendEvent('makeActions', 'd-v2-e10', 'offers', 'REWARD_COPY_LINK', eventData);
  }, [rewardInfo])

  if (!rewardData && !notLoaded && !isLoading) {
    return <RewardItemError link={link} rewardInfo={rewardInfo} />
  }

  return (
    <div className={classNames(classes.reward, {[classes.rewardZero]: rewardData?.commission === 0})}>
      {notLoaded || isLoading ? (
        <LinearProgress/>
      ) : (
        <>
          <Typography variant="body2">{rewardInfo.title}</Typography>
          <div className={classes.reward__title}>
            <Typography variant="h4">{rewardData?.commission}%</Typography>
            <Typography classes={{root: classes.breakall}} variant="subtitle2">{rewardData?.name ?? link}</Typography>
          </div>
          <div className={classes.reward__link}>
            {rewardData?.commission ? (
              <CopyTextField
                useShortener
                isShortLink={isShortLink}
                classes={{root: classes.copyTextField}}
                InputProps={{classes: {root: classes.copyTextField}}}
                value={linkToCode(link, code)}
                onCopyText={handleCopyText}
              />
            ) : (
              <Typography classes={{root: classes.noCommission}} variant="subtitle2">
                {rewardInfo.noCommission}
              </Typography>
            )}
          </div>
        </>
      )}
    </div>
  )
}

RewardItem.propTypes = {
  link: PropTypes.string,
  code: PropTypes.string,
  rewardInfo: PropTypes.object,
  isShortLink: PropTypes.bool
}

function RewardItemError({ link, rewardInfo }: TRewardItemError) {
  return (
    <Alert severity="error">
      <AlertTitle classes={{ root: classes.breakall }}>{link}</AlertTitle>
      {rewardInfo?.urlError}
    </Alert>
  )
}

RewardItemError.propTypes = {
  link: PropTypes.string,
  rewardInfo: PropTypes.object
}
